/**
 * Companies: every organisation the business deals with, whatever it is to
 * them. A company holds any of three roles: data buyer (it has a buyer
 * record routing sells to), data seller (a supplier record leads arrive
 * from) and customer (it has deals).
 *
 * `?role=data_buyer` and `?role=data_seller` are the Buyers and Suppliers
 * menu items: the same list filtered to a role, open to whoever had those
 * pages, and with a "New" button that creates the buyer or supplier record
 * (and its company) as those pages did. `/buyers` and `/suppliers` redirect
 * here.
 *
 * `?new=1` opens the new company form, which is how ⌘K reaches it.
 */
import { useState } from 'react';
import {
  Alert, Badge, Button, Card, Checkbox, Chip, Group, Loader, Modal, Pagination, Stack, Table, Text, TextInput,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCompanies, useCreateCompany, type CompanyRoleKey, type CompanySummary } from '../../api/crm';
import { FONTS } from '../../theme';
import { PageHeader } from '../../ui';
import { navLabel, useVocab } from '../../vocab';
import { formatPence, OwnerSelect, RoleChips, roleFromParam, roleWord, ROLE_KEYS, useCrmPermissions } from './crmShared';
import { BulkAssignBar, OwnerFilterSelect } from '../assignment/OwnerControls';
import { ColumnPicker, PropertyCells, PropertyHeaders, usePropertyColumns } from '../properties/PropertyColumns';
import { RequiredPropertyInputs, requiredValues } from '../properties/PropertyFields';
import { useProperties } from '../../api/properties';
import { EntityRecordModal } from '../EntityRecordModal';

const PAGE_SIZE = 50;

/** Whether a role's record is switched off, for the list's status. */
function roleOff(c: CompanySummary, role: CompanyRoleKey | null): boolean | null {
  if (role === 'data_buyer') return c.buyer ? !c.buyer.active : null;
  if (role === 'data_seller') return c.supplier ? !c.supplier.active : null;
  return null;
}

export function CompaniesPage() {
  const v = useVocab();
  const perms = useCrmPermissions();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [owner, setOwner] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [newRecord, setNewRecord] = useState(false);
  const role = roleFromParam(params.get('role'));
  // The roles this person may filter by: every one with Companies, else
  // only those of the Buyers and Suppliers pages they have.
  const crm = perms.seesCompanies;
  const roleShown = (r: CompanyRoleKey) =>
    r === 'data_buyer' ? perms.seesBuyers || crm : r === 'data_seller' ? perms.seesSuppliers || crm : crm;
  const offered = ROLE_KEYS.filter(roleShown);
  // Someone with Buyers but not Companies always looks at a role view.
  const effectiveRole: CompanyRoleKey | null = role && roleShown(role) ? role : crm ? null : offered[0] ?? null;
  const setRole = (r: CompanyRoleKey | null) => {
    const next = new URLSearchParams(params);
    if (r) next.set('role', r);
    else next.delete('role');
    setParams(next, { replace: true });
    setPage(1);
  };

  const list = useCompanies({
    q, page, page_size: PAGE_SIZE, ...(effectiveRole ? { role: effectiveRole } : {}), ...(owner ? { owner_user_id: owner } : {}),
  });
  const rows = list.data?.companies ?? [];
  const ticks = perms.assignRecords && crm;
  const cols = usePropertyColumns('company', perms.recordProperties && crm);
  const columns = perms.recordProperties && crm ? cols.columns : [];
  const creating = params.get('new') === '1';
  const closeNew = () => {
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  };
  const statusCol = effectiveRole === 'data_buyer' || effectiveRole === 'data_seller';
  const showDeals = crm && perms.seesDeals;
  const colCount = 3 + (ticks ? 1 : 0) + (statusCol ? 1 : 0) + (showDeals ? 1 : 0) + (perms.owners ? 1 : 0) + columns.length;

  // The title and the "New" button follow the view: Buyers is the Buyers
  // page as it was, now listing companies.
  const title = effectiveRole === 'data_buyer'
    ? navLabel(v, 'buyers')
    : effectiveRole === 'data_seller' ? navLabel(v, 'suppliers') : navLabel(v, 'companies');
  const newKind = effectiveRole === 'data_buyer' ? 'buyers' : effectiveRole === 'data_seller' ? 'suppliers' : null;
  const canNewRecord = newKind === 'buyers' ? perms.editBuyers : newKind === 'suppliers' ? perms.editSuppliers : false;

  return (
    <Stack gap="md">
      <PageHeader
        title={title}
        actions={newKind ? canNewRecord && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setNewRecord(true)}>
            {`New ${newKind === 'buyers' ? v.p.dataBuyerLower : v.p.dataSellerLower}`}
          </Button>
        ) : perms.editCompanies && crm && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setParams({ new: '1' }, { replace: true })}
          >
            {`New ${v.one('company')}`}
          </Button>
        )}
      />
      <Text size="sm" c="dimmed" maw={720}>
        {effectiveRole === 'data_buyer'
          ? `${v.Many('company')} that buy from us. Each has ${v.a('buyer')} record that routing sells to; open one for its ${v.many('destination')}, returns, billing and statements.`
          : effectiveRole === 'data_seller'
            ? `${v.Many('company')} that sell to us. Each has ${v.a('supplier')} record that ${v.many('lead')} arrive from; open one for its costs, return rate and statements.`
            : `One record per organisation. A ${v.one('company')} can hold several roles at once: ${v.p.dataBuyerLower}, ${v.p.dataSellerLower} and customer.`}
      </Text>

      <Group gap="sm" align="center" wrap="wrap">
        <TextInput
          aria-label="Search"
          placeholder="Name, domain or phone"
          leftSection={<IconSearch size={14} />}
          value={q}
          onChange={(e) => { setQ(e.currentTarget.value); setPage(1); }}
          w={260}
        />
        <Chip.Group
          multiple={false}
          value={effectiveRole ?? ''}
          onChange={(r) => setRole(r === (effectiveRole ?? '') ? null : (roleFromParam(r) ?? null))}
        >
          <Group gap={6} wrap="wrap" data-testid="role-filter">
            {crm && <Chip value="" size="sm" variant="outline">All</Chip>}
            {offered.map((r) => (
              <Chip key={r} value={r} size="sm" variant="outline">{roleWord(v, r)}</Chip>
            ))}
          </Group>
        </Chip.Group>
        {perms.owners && crm && (
          <OwnerFilterSelect value={owner} onChange={(o) => { setOwner(o); setPage(1); setPicked([]); }} />
        )}
        {perms.recordProperties && crm && (
          <div style={{ marginLeft: 'auto' }}>
            <ColumnPicker defs={cols.defs} chosen={cols.chosen} onChange={cols.setChosen} />
          </div>
        )}
      </Group>

      {list.error && <Alert color="red" variant="light">{list.error.message}</Alert>}
      {ticks && <BulkAssignBar kind="companies" ids={picked} onClear={() => setPicked([])} onDone={() => setPicked([])} />}

      <Card p={0}>
        {list.isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <Table verticalSpacing={8} highlightOnHover data-testid="companies-table">
              <Table.Thead>
                <Table.Tr>
                  {ticks && (
                    <Table.Th w={36}>
                      <Checkbox
                        aria-label="Select every row on this page"
                        checked={rows.length > 0 && rows.every((c) => picked.includes(c.id))}
                        indeterminate={picked.length > 0 && !rows.every((c) => picked.includes(c.id))}
                        onChange={(e) => setPicked(e.currentTarget.checked ? rows.map((c) => c.id) : [])}
                      />
                    </Table.Th>
                  )}
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Roles</Table.Th>
                  {statusCol && <Table.Th>Status</Table.Th>}
                  {perms.owners && <Table.Th>Owner</Table.Th>}
                  {showDeals && <Table.Th ta="right">{`Open ${v.many('deal')}`}</Table.Th>}
                  <PropertyHeaders columns={columns} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data?.companies.length === 0 && (
                  <Table.Tr><Table.Td colSpan={colCount}>
                    <Text ta="center" c="dimmed" py="lg">
                      {q || effectiveRole ? `No ${v.many('company')} match.` : `No ${v.many('company')} yet.`}
                    </Text>
                  </Table.Td></Table.Tr>
                )}
                {list.data?.companies.map((c) => {
                  const off = roleOff(c, effectiveRole);
                  const tab = effectiveRole === 'data_buyer' ? '?tab=buys' : effectiveRole === 'data_seller' ? '?tab=sells' : '';
                  return (
                    <Table.Tr key={c.id} onClick={() => navigate(`/companies/${c.id}${tab}`)} style={{ cursor: 'pointer' }}>
                      {ticks && (
                        <Table.Td onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            aria-label={`Select ${c.name}`}
                            checked={picked.includes(c.id)}
                            onChange={(e) => {
                              const on = e.currentTarget.checked;
                              setPicked((all) => (on ? [...all, c.id] : all.filter((x) => x !== c.id)));
                            }}
                          />
                        </Table.Td>
                      )}
                      <Table.Td>
                        <Text size="sm" fw={500} component={Link} to={`/companies/${c.id}${tab}`} onClick={(e) => e.stopPropagation()}>
                          {c.name}
                        </Text>
                        {c.domain && <Text size="xs" c="dimmed">{c.domain}</Text>}
                      </Table.Td>
                      <Table.Td>
                        <RoleChips
                          roles={c.role_keys}
                          archived={{ data_buyer: c.buyer?.active === false, data_seller: c.supplier?.active === false }}
                        />
                      </Table.Td>
                      {statusCol && (
                        <Table.Td>
                          {off != null && (
                            <Badge variant="light" color={off ? 'gray' : 'chartGreen'}>{off ? 'Off' : 'Active'}</Badge>
                          )}
                        </Table.Td>
                      )}
                      {perms.owners && (
                        <Table.Td>
                          {c.owner_name ? <Text size="sm">{c.owner_name}</Text> : <Text size="sm" c="dimmed">Nobody</Text>}
                        </Table.Td>
                      )}
                      {showDeals && (
                        <Table.Td ta="right">
                          <span style={{ fontFamily: FONTS.mono, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                            {c.open_deals > 0 ? `${c.open_deals} · ${formatPence(c.open_amount_pence)}` : '0'}
                          </span>
                        </Table.Td>
                      )}
                      <PropertyCells columns={columns} values={c.properties} />
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {(list.data?.pagination.total_pages ?? 0) > 1 && (
          <Group justify="flex-end" p="sm">
            <Pagination size="sm" total={list.data?.pagination.total_pages ?? 1} value={page} onChange={setPage} />
          </Group>
        )}
      </Card>

      {crm && (
        <NewCompanyModal
          opened={creating}
          onClose={closeNew}
          onCreated={(id) => navigate(`/companies/${id}`)}
        />
      )}
      {newKind && (
        <EntityRecordModal
          kind={newKind}
          entity={null}
          opened={newRecord}
          onClose={() => setNewRecord(false)}
          onSaved={(e) => {
            if (e.company_id) navigate(`/companies/${e.company_id}?tab=${newKind === 'buyers' ? 'buys' : 'sells'}`);
          }}
        />
      )}
    </Stack>
  );
}

function NewCompanyModal({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const v = useVocab();
  const create = useCreateCompany();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [phone, setPhone] = useState('');
  const [owner, setOwner] = useState<string | null>(null);
  const [props, setProps] = useState<Record<string, unknown>>({});
  const [error, setError] = useState('');
  const required = useProperties('company', { enabled: opened }).data?.properties.filter((d) => d.required && !d.archived);

  async function submit() {
    if (!name.trim()) {
      setError(`${v.A('company')} needs a name.`);
      return;
    }
    const properties = requiredValues(required, props);
    try {
      const c = await create.mutateAsync({
        name: name.trim(),
        ...(domain.trim() ? { domain: domain.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(owner ? { owner_user_id: owner } : {}),
        ...(properties ? { properties } : {}),
      });
      setName(''); setDomain(''); setPhone(''); setOwner(null); setProps({}); setError('');
      onCreated(c.id);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`New ${v.one('company')}`}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <TextInput label="Name" required data-autofocus value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <TextInput label="Domain" placeholder="example.co.uk" value={domain} onChange={(e) => setDomain(e.currentTarget.value)} />
        <TextInput label="Phone" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
        <OwnerSelect label="Owner" value={owner} onChange={setOwner} />
        <RequiredPropertyInputs object="company" values={props} onChange={setProps} />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={create.isPending}>Create</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
